/**
 * Getting figures OUT of the app — the one place data leaves the encrypted copy.
 *
 * Everything else here rests on the promise that the books exist decrypted in
 * exactly two places. A share sheet breaks that on purpose, at the user's
 * request, which is why it sits behind the `share` flag and why the text it
 * produces is deliberately *narrow*: one party's statement, one outstanding
 * list, one report. There is no "share everything".
 *
 * Two mechanisms, because phones and desktops differ:
 *   - `navigator.share` on a phone: the real share sheet, WhatsApp included.
 *   - a clipboard copy or a file download everywhere else.
 *
 * Amounts are formatted for a HUMAN reading a message, not for a machine
 * re-importing them. A recipient of a payment reminder should see
 * "₹1,25,400.00", not "-125400".
 */

/** Indian grouping, two decimals, no currency symbol swallowed by the locale. */
export function money(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
}

function day(iso: string): string {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

export type ShareResult = "shared" | "copied" | "cancelled" | "unavailable";

/**
 * Share text, falling back to the clipboard.
 *
 * A cancelled share sheet is NOT an error — the user changed their mind, and
 * telling them "sharing failed" for that is both wrong and alarming when the
 * subject is their own accounts.
 */
export async function shareText(title: string, body: string): Promise<ShareResult> {
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text: body });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
      // Fall through: some browsers reject share() for reasons other than the
      // user cancelling, and the clipboard still gets the job done.
    }
  }

  try {
    await navigator.clipboard.writeText(body);
    return "copied";
  } catch {
    return "unavailable";
  }
}

/**
 * Download rows as CSV.
 *
 * Excel decides a file's encoding from a BOM; without one, a party called
 * "Kumār" or any ₹ sign arrives as mojibake, and an accountant's first
 * impression of the export is that it is broken.
 */
export function downloadCsv(filename: string, rows: Array<Array<string | number>>): void {
  const body = rows
    .map((row) =>
      row
        .map((cell) => {
          const text = String(cell ?? "");
          return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        })
        .join(","),
    )
    .join("\r\n");

  const blob = new Blob([`﻿${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking immediately can cancel the download on some Android browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/* ─────────────────────────────────────────────────────── message builders ── */

export type StatementLine = {
  date: string;
  voucherType: string;
  voucherNumber: string;
  amount: number;
  balance: number;
};

/**
 * A party statement as a message.
 *
 * Capped at the most recent 30 lines: a WhatsApp message with 400 rows in it is
 * not read by anybody, and the full history is a CSV away.
 */
export function statementMessage(options: {
  company: string;
  party: string;
  closing: number;
  lines: StatementLine[];
  asOf: string;
  limit?: number;
}): string {
  const { company, party, closing, asOf } = options;
  const limit = options.limit ?? 30;
  const recent = options.lines.slice(-limit);
  const omitted = options.lines.length - recent.length;

  const rows = recent.map(
    (l) =>
      `${day(l.date)}  ${l.voucherType} ${l.voucherNumber}`.trim() +
      `  ${l.amount < 0 ? "Dr" : "Cr"} ${money(l.amount)}`,
  );

  // Debit-negative throughout (CLAUDE.md §5). For a customer, a debit balance
  // is what they owe us — so the wording flips, not the sign.
  const owed =
    closing < 0
      ? `They owe ${money(closing)}`
      : closing > 0
        ? `We owe ${money(closing)}`
        : "Settled — nothing outstanding";

  return [
    `${party}`,
    `Statement as on ${day(asOf)}`,
    ``,
    ...(omitted > 0 ? [`(showing the last ${recent.length} of ${options.lines.length} entries)`, ``] : []),
    ...rows,
    ``,
    `Closing: ${owed}`,
    ``,
    `— ${company}`,
  ].join("\n");
}

export type ReminderBill = { billName: string; dueDate: string; amount: number; daysOverdue: number };

/**
 * A payment reminder.
 *
 * Written to be sent as-is to a customer, which means it must be polite, must
 * not accuse, and must carry enough detail (bill numbers, dates) that the
 * recipient can act without replying to ask which invoice you mean.
 */
export function reminderMessage(options: {
  company: string;
  party: string;
  total: number;
  bills: ReminderBill[];
  asOf: string;
}): string {
  const { company, party, total, bills, asOf } = options;
  const overdue = bills.filter((b) => b.daysOverdue > 0);

  const rows = bills
    .slice()
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
    .slice(0, 20)
    .map(
      (b) =>
        `${b.billName}  ${money(b.amount)}  due ${day(b.dueDate)}` +
        (b.daysOverdue > 0 ? `  (${b.daysOverdue} days overdue)` : ""),
    );

  return [
    `Dear ${party},`,
    ``,
    `A gentle reminder about the following, outstanding as on ${day(asOf)}:`,
    ``,
    ...rows,
    ...(bills.length > 20 ? [`… and ${bills.length - 20} more`] : []),
    ``,
    `Total outstanding: ${money(total)}` +
      (overdue.length > 0 ? `, of which ${overdue.length} bill${overdue.length === 1 ? " is" : "s are"} past due.` : "."),
    ``,
    `Please ignore this message if payment has already been sent.`,
    ``,
    `Regards,`,
    `${company}`,
  ].join("\n");
}

export function outstandingMessage(options: {
  company: string;
  kind: "receivable" | "payable";
  parties: Array<{ ledger: string; total: number }>;
  total: number;
  asOf: string;
  limit?: number;
}): string {
  const { company, kind, parties, total, asOf } = options;
  const limit = options.limit ?? 25;
  const top = parties.slice(0, limit);

  return [
    kind === "receivable" ? "Receivables" : "Payables",
    `As on ${day(asOf)}`,
    ``,
    ...top.map((p) => `${p.ledger}  ${money(p.total)}`),
    ...(parties.length > limit ? [`… and ${parties.length - limit} more`] : []),
    ``,
    `Total: ${money(total)}`,
    ``,
    `— ${company}`,
  ].join("\n");
}
